/* eslint-disable camelcase */
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { TokenSnapshot } from "../token/token"
import { getGeneralApiProblem } from "../../services/api/api-problem"
import { Farm, User } from "../../database"
import { Platform } from "react-native"
import _ from "lodash"
import { Toast } from "../../components"
import I18n from "i18n-js"
import SyncableModel from "../../database/SyncableModel"
import ImageResizer from "react-native-image-resizer"
import JalaAnalytic from "../../utils/jala-analytic"

const UserStoreProps = {
  loading: types.maybeNull(types.boolean),

  error: types.maybeNull(types.frozen()),

  user: types.maybeNull(types.frozen()),
  connectedFarm: types.optional(types.array(types.frozen()), []),
  responseImage: types.maybeNull(types.frozen()),

  queryingUser: types.maybeNull(types.boolean),
  syncingUser: types.maybeNull(types.boolean),
  syncedUser: types.maybeNull(types.boolean),
  skipCoachmark: types.maybeNull(types.boolean),
  isCoachmarkRunning: types.maybeNull(types.boolean)
}

/**
 * Model description here for TypeScript hints.
 */

export const UserStoreModel = types
  .model("UserStore")
  .props(UserStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .views((self) => ({
    hasInterest(interest) {
      const interests = ((self.user || {}).interests || []);
      if (!interests.length && interest === 'management') return true;
      return interests.includes(interest);
    },
    get hasManagementInterest() {
      return this.hasInterest('management')
    },
    get userLocale() {
      return self.user?.settings?.locale || I18n.locale
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof UserStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    async getUser(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingUser', true);
        const user = await self.axios.get('/profile').then((response) => {
          const { data: { data } } = response
          return data;
        })
        const email = user.email || `${user.phone.replace("+", "")}@jala.tech`
        this.set('user', {
          ...user,
          email
        });
        this.set('queryingUser', false);
      }
      else {
        this.set('queryingUser', true);
        this.syncUser().then(async () => {
          await this.getUser(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedUser', true);
          this.set('queryingUser', false);
        })
      }
    },
    async syncUser() {
      const { user } = self;
      if (Platform.OS === 'web' || !(user && user.id))
        return true;

      this.set('syncingUser', true)
      const success = await User.syncAll(
        { id__in: user.id },
        [['users.id', [user.id]]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )
      this.set('syncingUser', false)
      return success
    },
    init() {
      this.set('loading', false)
      this.set('error', null)
      this.set('responseImage', null)
    },
    // async update(data) {
    async updateData(data) {
      return self.axios.put('/profile', data).then(() => true).catch((e) => {
        if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        throw e;
      });
    },
    async updateLocale(locale?: "en"|"id"|"es"|"vi") {
      // return this.update({ locale: locale || I18n.locale });
      return this.updateData({ locale: locale || I18n.locale }).catch(() => false);
    },
    async register(user: any) {
      const { tokenStore } = self.rootStore;
      this.set('loading', true)
      const result: any = await self.apiSauce.post("/register", user)
      this.set('loading', false)

      if (result.status === 201) {
        const { meta: { access_token: accessToken } } = result.data;
        this.set('error', null)
        await tokenStore.setToken({ access_token: accessToken } as TokenSnapshot)
        await this.getUser()
      } else {
        // __DEV__ && console.tron.log(result.status)
        this.set('user', null)
        this.set('error', getGeneralApiProblem(result))
      }
    },
  }))
  .actions((self) => ({
    async changePassword(params) {
      const { tokenStore: { token } } = self.rootStore;

      self.set('loading', true);
      const result: any = await self.apiSauce.post("/profile", params,
        { headers: { Authorization : `Bearer ${token.access_token}` } }
      )


      if (result.status === 200) {
        const { data } = result.data;
        self.set('error', null)
        self.set('user', data)
        self.set('loading', false);
      } else {
        // __DEV__ && console.tron.log(result.status)
        self.set('error', getGeneralApiProblem(result))
        self.set('loading', false);
      }
    },

    async deleteAccount(params) {
      const { tokenStore: { token } } = self.rootStore;
      const { id } = self.user

      self.set('loading', true);
      const result: any = await self.environment.api.apisauce.delete(`/users/${id}`, params,
        {
          headers: { Authorization : `Bearer ${token.access_token}` },
          data: { password_old: params }
        }
      )

      if (result.status === 200) {
        JalaAnalytic.log('Mobile app: account - deleted', { id })

        self.set('error', null);
        self.set('loading', false);
        self.rootStore.reset();
      } else {
        // __DEV__ && console.tron.log(result.status)
        self.set('error', getGeneralApiProblem(result))
        self.set('loading', false);
      }
    },

    createFormData(key, image, body) {
      const data = new FormData();
      if (image) {
          data.append(key, {
            name: image.response.name,
            type: image.type,
            uri: image.response.uri
          } as any)
      }
      Object.keys(body).forEach((item) => {
        if (_.isObject(body[item])) {
          return Object.keys(body[item]).forEach((k) => {
            data.append(`${item}[${k}]`, body[item][k] || '');
          });
        }
        return data.append(item, body[item] || '');
      });
      return data
    },

    async updateProfile(params, data = null) {
      const { tokenStore: { token } } = self.rootStore;
      self.set('loading', true);
      this.createFormData('avatar', data, params)

      const result: any = await self.apiSauce.post("/profile", params,
        {
          headers: { Authorization : `Bearer ${token.access_token}` },
          data: this.createFormData('avatar', data, params)
         }
      )
      if (result.status === 200) {
        const { data } = result.data;

        JalaAnalytic.log('Mobile app: account - edit account', params);

        self.set('error', null)
        self.set('user', data)
        self.set('loading', false)
        self.set('responseImage', null)
      } else {
        // __DEV__ && console.tron.log(result.status)
        self.set('error', {
          ...getGeneralApiProblem(result),
          message: result?.data?.message || result?.originalError?.message || ""
        })
        self.set('loading', false);
      }

      return true
    },

    async fetchFarmProfile(farmId) {
      const { tokenStore: { token }, farmStore } = self.rootStore;
      const userId = '';
      self.set('loading', true);

      const result: any = await self.apiSauce.get(`/farm_users?with=farm.region.country,role,user&per_page=1000${userId ? `&user_id__in=${userId}` : ''}${farmId ? `&farm_id__in=${farmId}` : ''}`, {},
        {
          headers: { Authorization : `Bearer ${token.access_token}` }
        }
      )

      if (result.status === 200) {
        const { data } = result.data;
        self.set('connectedFarm', _.orderBy(data, ['farm.name'], ['asc']));
        farmStore.set('needRefresh', false);
        self.set('error', null);
        self.set('loading', false);
      } else {
        // __DEV__ && console.tron.log(result.status)
        self.set('error', getGeneralApiProblem(result))
        self.set('loading', false);
      }
    },

    getFarmProfile(){
      Farm.fetch().then((farms: Array<Farm>) => {
        const farmId = farms.map((farm: Farm) => farm.getKey()).join(',');
        return this.fetchFarmProfile(farmId)
      })
    },

    async updateInterest(data){
      const { tokenStore: { token } } = self.rootStore;
      self.set('loading', true);

      const result: any = await self.environment.api.apisauce.put("/profile", {},
        {
          headers: { Authorization : `Bearer ${token.access_token}` },
          data: data
        }
      )

      if (result.status === 200) {
        // const { data } = result.data;
        self.set('error', null)
        self.set('loading', false);
      } else {
        // __DEV__ && console.tron.log(result.status)
        self.set('error', getGeneralApiProblem(result))
        self.set('loading', false);
      }
    },
    async update(model: SyncableModel, data) {
      const params = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        gender: data.gender,
        region_id: data.region_id,
        occupation: data.occupation,
        birthdate: data.birthdate,
        state: { ...data.state },
        _method: 'PUT'
      }
      const { responseImage } = self
      if (responseImage) {
        ImageResizer.createResizedImage(responseImage.assets[0].uri, 300, 300, 'JPEG', 80).then((resp) => {
          const photo = { response: resp, type: responseImage.assets[0].type };
          return this.updateProfile(params, photo)
        })
      }

      return this.updateProfile(params, null)

    }
  }))

type UserStoreType = Instance<typeof UserStoreModel>
export interface UserStore extends UserStoreType {}
type UserStoreSnapshotType = SnapshotOut<typeof UserStoreModel>
export interface UserStoreSnapshot extends UserStoreSnapshotType {}
export const createUserStoreDefaultModel = () => types.optional(UserStoreModel, {})
